import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { TranslationKey } from '../../i18n/translationKeys';
import { Banner } from '../../ui/Banner/Banner';

/**
 * Shown when the bridge is reachable but no session is open. The desktop bridge authenticates itself —
 * Acomba pushes the open société's credentials to it when ComptabilitéAI is launched — so there is no
 * in-app credential entry; recovery is to reopen ComptabilitéAI from Acomba.
 *
 * Cases:
 *  - SDK unavailable: the bridge is running but the Acomba SDK/COM components are not installed/registered
 *    on this machine → distinct, actionable "install the Acomba SDK" guidance.
 *  - SDK version mismatch: the installed Acomba is too old for this company file → distinct guidance that
 *    reports the installed SDK version (from the /health probe) when it is known.
 *  - Any other not-connected state: a generic notice with the last session error (when present) and the
 *    reconnect hint.
 *
 * The two SDK cases carry the danger tone because nothing works until the machine is fixed; a plain
 * not-connected session is recoverable by relaunching, so it warns instead.
 */
interface SessionReasonBannerProps {
    onRetry?: () => void;
    retrying?: boolean;
    /** Icon-on-top, centered layout for a full-page state — see Banner's own `centered` prop. */
    centered?: boolean;
}

export const SessionReasonBanner: React.FC<SessionReasonBannerProps> = ({ onRetry, retrying, centered }) => {
    const { t } = useTranslation();
    const sessionReason = useAcombaBridgeStore((s) => s.sessionReason);
    const sessionErrorMessage = useAcombaBridgeStore((s) => s.sessionErrorMessage);
    // The installed SDK version comes from the /health probe and stays populated even when the session
    // fails, so the version-mismatch copy can report which version is actually installed.
    const bridgeInfo = useAcombaBridgeStore((s) => s.bridgeInfo);

    const action = onRetry
        ? {
            label: t(TranslationKey.AcombaBridge.Wizard.RetryBtn),
            onClick: onRetry,
            testId: 'acomba-bridge-session-retry-btn',
            disabled: retrying,
        }
        : undefined;

    if (sessionReason === 'SdkUnavailable') {
        return (
            <Banner
                tone="danger"
                testId="acomba-bridge-sdk-unavailable-banner"
                title={t(TranslationKey.AcombaBridge.Status.SdkUnavailable)}
                action={action}
                centered={centered}
            >
                {t(TranslationKey.AcombaBridge.Status.SdkUnavailableDetail)}
            </Banner>
        );
    }

    if (sessionReason === 'SdkVersionMismatch') {
        // Only surface the "(SDK version X)" parenthetical when a version is actually known — otherwise the
        // copy would read "...on this machine ()". bridgeInfo is populated on the normal path, so this is
        // defensive against the unusual case where the banner shows before /health has resolved.
        const version = bridgeInfo?.sdkVersion;
        const versionSuffix = version
            ? t(TranslationKey.AcombaBridge.Status.SdkVersionMismatchVersionSuffix, { version })
            : '';
        return (
            <Banner
                tone="danger"
                testId="acomba-bridge-sdk-version-mismatch-banner"
                title={t(TranslationKey.AcombaBridge.Status.SdkVersionMismatch)}
                action={action}
                centered={centered}
            >
                {t(TranslationKey.AcombaBridge.Status.SdkVersionMismatchDetail, { versionSuffix })}
            </Banner>
        );
    }

    return (
        <Banner
            tone="warning"
            testId="acomba-bridge-not-connected-banner"
            title={t(TranslationKey.AcombaBridge.Status.NotConnected)}
            action={action}
            centered={centered}
        >
            {sessionErrorMessage && (
                <p data-testid="acomba-bridge-session-error">{t(sessionErrorMessage)}</p>
            )}
            <p>{t(TranslationKey.AcombaBridge.Status.ReconnectFromAcomba)}</p>
        </Banner>
    );
};
